import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Chip,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material'
import { lighten } from '@mui/material/styles'
import { IconSeeker } from '@ui-seeker/core'
import { BarChart }   from '@mui/x-charts/BarChart'
import { LineChart }  from '@mui/x-charts/LineChart'
import { MY_BUSINESS_ROUTES } from '@constants/routes'
import { AppCardTitle, AppEmptyLine, AppFilterPanel, AppPillToggle, AppResultsSkeleton, AppNote, AppValueChip } from '@components'
import { dsColorNew } from '@palette'
import { MbPage, MbKpiCard, MbKpiRow, MbPageSkeleton } from 'Pages/MyBusiness/_shared/Ui'

import {
	ApplyButton,
	DateField,
	DatePresets,
	DateRangeField,
	FORWARD_PRESETS,
	OfficeSlicer,
	CategorySlicer,
	mbFieldsGridSx,
} from 'Pages/MyBusiness/_shared/Filters'
import { buildTickFormatter, pickTickNumber, DownloadCsvButton } from 'Pages/MyBusiness/_shared/Charts'
import { displayDay } from '@utils'

// CSV download endpoint shares the same offices/sipps/date semantics
// as the main /forecast query but the raw rows come from fact_reservations_otb,
// so we only need date_from/to and the slicers (no cut_date, no activity).
function forecastCsvQs(f) {
	const qs = new URLSearchParams({ from: f.date.from, to: f.date.to })
	if (f.offices.length)    qs.set('offices', f.offices.join(','))
	if (f.categories.length) qs.set('sipps',   f.categories.join(','))
	return qs.toString()
}

/**
 * A4 — Forecast & OTB.
 *
 * Reproduces the POC fleet projection: historic peaks (pre cut_date)
 * + projected demand (post cut_date) merged into a single timeline,
 * with a clear visual divider on the cut date and a colour change
 * between historic and projected segments.
 *
 * Backend: GET /api/my-business/forecast?cut_date=&from=&to=&
 *                                        activity_type=&offices=&sipps=
 */
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)
const iso = (d) => d.toISOString().slice(0, 10)

const ACTIVITY_OPTIONS = [
	{ value: 'rented',      label: 'Rented' },
	{ value: 'idle',        label: 'Available' },
	{ value: 'maintenance', label: 'Maintenance' },
]

function defaults() {
	// Forecast is forward-looking by design: cut = today, range = next
	// 60 days. The user can rewind `from` past the cut_date if they
	// want recent historic as context, but we don't show it by default
	// because the "OTB-only projection" vs "real historic peak" gap
	// reads as a confusing cliff when they're side by side.
	const today = new Date()
	const fut   = new Date(today); fut.setDate(fut.getDate() + 60)
	return {
		cut_date:      iso(today),
		date:          { preset: 'next60', from: iso(today), to: iso(fut) },
		activity_type: 'rented',
		offices:       [],
		categories:    [],
	}
}

export default function MyBusinessForecastPage() {
	const [applied, setApplied] = useState(defaults)
	const [draft,   setDraft]   = useState(defaults)
	const [data,    setData]    = useState(null)
	const [error,   setError]   = useState(null)
	const [loading, setLoading] = useState(true)
	const pageLoading = loading

	const fetchForecast = useCallback((f) => {
		setLoading(true); setError(null)
		const qs = new URLSearchParams({
			cut_date:      f.cut_date,
			from:          f.date.from,
			to:            f.date.to,
			activity_type: f.activity_type,
		})
		if (f.offices.length)    qs.set('offices', f.offices.join(','))
		if (f.categories.length) qs.set('sipps',   f.categories.join(','))
		fetch(`/api/my-business/forecast?${qs}`, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [])

	// Activity + slicers + cut date + date presets apply instantly;
	// manual date typing waits for Apply.
	const applyInstant = (patch) => {
		const next = { ...applied, ...patch }
		setApplied(next); setDraft(next); fetchForecast(next)
	}
	const applyManual = () => {
		setApplied(draft); fetchForecast(draft)
	}
	// DateRangeFilter emits { intent: 'instant'|'pending' }; route accordingly.
	const onDateChange = (date, opts) => {
		if (opts?.intent === 'instant') applyInstant({ date })
		else setDraft({ ...draft, date })
	}

	useEffect(() => { fetchForecast(applied) }, [])  // initial only

	const hasPending = useMemo(() => (
		applied.cut_date !== draft.cut_date ||
		applied.date.from !== draft.date.from ||
		applied.date.to   !== draft.date.to
	), [applied, draft])

	return (
		<MbPage title={MY_BUSINESS_ROUTES.FORECAST_TITLE}
		        subtitle={MY_BUSINESS_ROUTES.FORECAST_SUBTITLE}>
			<Box>
				<AppFilterPanel
					toggles={<DatePresets value={draft.date}
					                      onChange={onDateChange}
					                      presets={FORWARD_PRESETS}/>}
					togglesRight={<AppPillToggle size="small"
					                             value={applied.activity_type}
					                             options={ACTIVITY_OPTIONS}
					                             onChange={(v) => v && applyInstant({ activity_type: v })}/>}
					actions={<>
						<DownloadCsvButton
							href={`/api/my-business/forecast/download?${forecastCsvQs(applied)}`}
							from={applied.date.from}
							to={applied.date.to}
							label="OTB CSV"/>
						<ApplyButton onClick={applyManual}
						             disabled={!hasPending || loading}>
							{loading ? 'Loading…' : hasPending ? 'Apply' : 'Up to date'}
						</ApplyButton>
					</>}
				>
					<Box sx={mbFieldsGridSx()}>
						<DateField label="Cut date"
						           value={draft.cut_date}
						           onChange={(cut_date) => applyInstant({ cut_date })}/>
						<DateRangeField value={draft.date}
						                onChange={onDateChange}/>
						<OfficeSlicer value={applied.offices}
						              onChange={(offices) => applyInstant({ offices })}/>
						<CategorySlicer value={applied.categories}
						                onChange={(categories) => applyInstant({ categories })}/>
					</Box>
				</AppFilterPanel>


				{error && <AppNote severity="error" sx={{ mb: 2 }}>Backend unreachable: {error}</AppNote>}

				{pageLoading && <MbPageSkeleton kpis={4} blocks={[420, 'list']}/>}

				{data && !pageLoading && (
					<Box>
						<KpiBanner kpis={data.kpis} cut={data.cut_date} activity={data.activity_type}/>
						<Box sx={{ mt: 3 }}>
							<ForecastChart data={data.data}
							               breakdown={data.breakdown}
							               referencePy={data.reference_py}
							               otbPy={data.otb_py}
							               cut={data.cut_date}
							               activity={data.activity_type}/>
						</Box>
						<Box sx={{ mt: 3 }}>
							<DetailsTable data={data.data}
							              referencePy={data.reference_py}
							              activity={data.activity_type}/>
						</Box>
					</Box>
				)}
			</Box>
		</MbPage>
	)
}

// ---------------------------------------------------------------------------
// KPI banner
// ---------------------------------------------------------------------------
function KpiBanner({ kpis, cut, activity }) {
	const gap = kpis.gap_vs_py_pct
	const gapStr = gap == null ? '—' : `${gap > 0 ? '+' : ''}${gap}%`
	const cards = [
		{ label: 'Cut date',           value: displayDay(cut),                            sub: 'historic / projected split',       icon: 'calendar-day' },
		{ label: 'Avg projected',      value: fmtNum(kpis.peak_projected),    sub: `${activity} · daily avg`,          icon: 'chart-line' },
		{ label: 'Same days PY',       value: fmtNum(kpis.avg_py),            sub: 'reference (year ago)',             icon: 'clock-rotate-left' },
		{ label: 'Pace vs PY',         value: gapStr,                         sub: 'sum projected ÷ sum PY same days', icon: 'gauge-high' },
	]
	return (
		<MbKpiRow>
			{cards.map((c) => (
				<MbKpiCard key={c.label}
				           icon={c.icon}
				           label={c.label}
				           value={c.value}
				           hint={c.sub}/>
			))}
		</MbKpiRow>
	)
}

// ---------------------------------------------------------------------------
// Chart — split historic vs projected as two series
// ---------------------------------------------------------------------------
const STATE_COLORS = {
	rented:      dsColorNew.dangerRed,
	maintenance: dsColorNew.warningBar,
	idle:        dsColorNew.textSubtle,
}

// Single-metric series: historic in navy, projected in the period blue, PY and OTB PY as references
const SERIES_COLORS = {
	hist:  dsColorNew.tableHead,
	proj:  dsColorNew.webBlue500,
	py:    dsColorNew.textSubtle,
	otbpy: dsColorNew.warningBar,
}

function ForecastChart({ data, breakdown, referencePy, otbPy, cut, activity }) {
	const [view, setView] = useState('single')   // 'single' | 'stacked'

	if (!data?.length) {
		return (
			<Paper sx={{ p: 3 }}>
				<AppEmptyLine>No data in this window.</AppEmptyLine>
			</Paper>
		)
	}

	const dates = data.map(d => d.date)
	const tickFormat = buildTickFormatter(dates)
	const tickNumber = pickTickNumber(dates)
	// Four series. Historic + Projected are split by `kind`; PY
	// reference spans the whole window aligned by date; OTB PY overlays
	// the projected line to show "last year's OTB at same lead time".
	const histData = data.map(d => d.kind === 'historic'  ? d.value : null)
	const projData = data.map(d => d.kind === 'projected' ? d.value : null)
	const pyMap = new Map((referencePy || []).map(r => [r.date, r.value]))
	const pyData = dates.map(d => pyMap.has(d) ? pyMap.get(d) : null)
	const otbPyMap = new Map((otbPy || []).map(r => [r.date, r.value]))
	// Only draw OTB PY on the projected segment — it's a "what you had
	// on the books last year at this same lead time" overlay, so on
	// historic days it doesn't add signal (reference_py already
	// carries the closed peak).
	const otbPyData = data.map(d => (d.kind === 'projected' && otbPyMap.has(d.date))
		? otbPyMap.get(d.date) : null)

	const labelMap = {
		rented:      'Vehicles rented',
		idle:        'Vehicles available',
		maintenance: 'Vehicles in maintenance',
	}
	const yLabel = labelMap[activity] || 'Vehicles'

	// The legend under the chart, with the line styles of each series (the chart's own legend stays hidden)
	const legend = (
		<Box display="flex" gap={2.5} alignItems="center" justifyContent="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
			{view === 'single' ? (
				<>
					<SeriesDot color={SERIES_COLORS.hist} label="Historic"/>
					<SeriesDot color={SERIES_COLORS.proj} label={`Projected · ${displayDay(cut)}`}/>
					<SeriesDot color={SERIES_COLORS.py} label="Same days PY" dashed/>
					<SeriesDot color={SERIES_COLORS.otbpy} label="OTB PY (same lead)" dashed/>
				</>
			) : (
				<>
					<SeriesDot color={STATE_COLORS.rented}      label="Rented"/>
					<SeriesDot color={STATE_COLORS.maintenance} label="Maintenance"/>
					<SeriesDot color={STATE_COLORS.idle}        label="Idle"/>
				</>
			)}
		</Box>
	)

	return (
		<Paper sx={{ p: 2.5 }}>
			<Box display="flex" alignItems="center" justifyContent="space-between"
			     flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
				<AppCardTitle icon="chart-area" title={`Forecast — ${yLabel.toLowerCase()}`}>
					<Tooltip arrow placement="right"
					         componentsProps={{ tooltip: { sx: { maxWidth: 360, p: 1.5 } }}}
					         title={
						<Box>
							<Box sx={{ fontSize: 13, mb: 0.75 }}>
								<Box component="span" sx={{ color: lighten(SERIES_COLORS.hist, 0.6), fontWeight: 700 }}>Historic</Box>:
								real recorded daily peak of {activity} vehicles before the
								cut date.
							</Box>
							<Box sx={{ fontSize: 13, mb: 0.75 }}>
								<Box component="span" sx={{ color: lighten(SERIES_COLORS.proj, 0.35), fontWeight: 700 }}>Projected</Box>:
								bookings already on the books as of {displayDay(cut)}. Does NOT
								anticipate walk-ins or last-minute reservations, so it
								always reads lower than the past would.
							</Box>
							<Box sx={{ fontSize: 13, mb: 0.75 }}>
								<Box component="span" sx={{ color: lighten(SERIES_COLORS.py, 0.3), fontWeight: 700 }}>Same days PY</Box>:
								the CLOSED peak the same day of last year. Benchmark
								for "what does this day usually reach".
							</Box>
							<Box sx={{ fontSize: 13 }}>
								<Box component="span" sx={{ color: SERIES_COLORS.otbpy, fontWeight: 700 }}>OTB PY (same lead time)</Box>:
								what you had on the books LAST year at this same
								anticipation. Compares your booking pace against a
								year ago — Projected above OTB PY = ahead of pace.
							</Box>
						</Box>
					}>
						<Box component="span" sx={{ display: 'inline-flex', cursor: 'help', ml: -0.5 }}>
							<IconSeeker icon="circle-question" sx={{ fontSize: 14, color: dsColorNew.textMuted }}/>
						</Box>
					</Tooltip>
				</AppCardTitle>
				<Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
					<ToggleButtonGroup size="small" exclusive value={view}
					                   onChange={(_, v) => v && setView(v)}>
						<ToggleButton value="single">Single metric</ToggleButton>
						<ToggleButton value="stacked"
						              disabled={!breakdown?.length}>3-state stack</ToggleButton>
					</ToggleButtonGroup>
				</Box>
			</Box>
			{view === 'single' ? (
				<LineChart
					xAxis={[{ data: dates, scaleType: 'point',
					          valueFormatter: tickFormat,
					          tickNumber }]}
					series={[
						{ data: pyData,    label: 'Same days PY',   color: SERIES_COLORS.py,
						  connectNulls: true, showMark: false, curve: 'monotoneX',
						  id: 'py' },
						{ data: otbPyData, label: 'OTB PY (same lead)', color: SERIES_COLORS.otbpy,
						  connectNulls: true, showMark: false, curve: 'monotoneX',
						  id: 'otbpy' },
						{ data: histData,  label: 'Historic',        color: SERIES_COLORS.hist,
						  connectNulls: false, showMark: false, area: true,
						  curve: 'monotoneX', id: 'hist' },
						{ data: projData,  label: 'Projected',       color: SERIES_COLORS.proj,
						  connectNulls: false, showMark: false, area: true,
						  curve: 'monotoneX', id: 'proj' },
					]}
					sx={{
						// PY: dashed, benchmark of the chart.
						'& .MuiLineElement-series-py': {
							strokeDasharray: '6 4',
							strokeWidth: 2.5,
						},
						// OTB PY: dashed amber, pace overlay on projected only.
						'& .MuiLineElement-series-otbpy': {
							strokeDasharray: '4 3',
							strokeWidth: 2,
						},
						// Soften the area fills so the colours don't drown the PY line.
						'& .MuiAreaElement-series-hist': { fillOpacity: 0.18 },
						'& .MuiAreaElement-series-proj': { fillOpacity: 0.22 },
					}}
					height={420}
					margin={{ top: 16, right: 16, bottom: 36, left: 60 }}
					slotProps={{ legend: { hidden: true } }}
				/>
			) : (
				<StackedForecastChart legend={legend}
				                      breakdown={breakdown}
				                      cut={cut}
				                      tickFormat={tickFormat}
				                      tickNumber={tickNumber}/>
			)}
			{view === 'single' && legend}
		</Paper>
	)
}

function StackedForecastChart({ legend, breakdown, cut, tickFormat, tickNumber }) {
	// Align everything on a shared X axis sorted by date.
	const sorted = [...breakdown].sort((a, b) => a.date.localeCompare(b.date))
	const dates  = sorted.map(d => d.date)
	const series = ['rented', 'maintenance', 'idle'].map(state => ({
		data:         sorted.map(d => d[state] || 0),
		label:        state[0].toUpperCase() + state.slice(1),
		color:        STATE_COLORS[state],
		stack:        'fleet',
		area:         true,
		showMark:     false,
		curve:        'monotoneX',
		id:           state,
		valueFormatter: (v) => v == null ? '—' : new Intl.NumberFormat('es-ES').format(v),
	}))
	// Visual divider between historic and projected — first index that
	// crosses into the projected segment.
	const cutIdx = sorted.findIndex(d => d.kind === 'projected')

	return (
		<>
			<LineChart
				xAxis={[{ data: dates, scaleType: 'point',
				          valueFormatter: tickFormat, tickNumber }]}
				series={series}
				sx={{
					'& .MuiAreaElement-series-rented':      { fillOpacity: 0.85 },
					'& .MuiAreaElement-series-maintenance': { fillOpacity: 0.85 },
					'& .MuiAreaElement-series-idle':        { fillOpacity: 0.55 },
				}}
				height={420}
				margin={{ top: 16, right: 16, bottom: 36, left: 60 }}
				slotProps={{ legend: { hidden: true } }}
			/>
			{legend}
			<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mt: 0.5 }}>
				Each band's height is the count of vehicles in that state on
				that day; the stack's total is your full fleet. Past days
				(left of {displayDay(cut)}) come from real recorded peaks; projected days
				(right) are built from your fleet snapshot + on-the-books
				reservations.
				{cutIdx > 0 && <> Cut date column: {displayDay(sorted[cutIdx].date)}.</>}
			</Typography>
		</>
	)
}

function SeriesDot({ color, label, dashed = false }) {
	return (
		<Box display="flex" alignItems="center" gap={0.75}
		     sx={{ fontSize: 12.5 }}>
			<Box sx={{
				width: 22, height: 3,
				bgcolor: dashed ? 'transparent' : color,
				borderTop: dashed ? `3px dashed ${color}` : 'none',
				borderRadius: 1,
			}}/>
			<Box component="span" sx={{ color: dsColorNew.textDark, fontWeight: 500 }}>
				{label}
			</Box>
		</Box>
	)
}

// ---------------------------------------------------------------------------
// Details table — daily numeric breakdown of the forecast chart.
// ---------------------------------------------------------------------------
function DetailsTable({ data, referencePy, activity }) {
	const pyMap = new Map((referencePy || []).map(r => [r.date, r.value]))
	const rows = data.map(d => {
		const py = pyMap.get(d.date)
		const delta = (py != null && py !== 0)
			? Math.round((d.value - py) / py * 1000) / 10
			: null
		return { ...d, py, delta }
	})

	return (
		<Accordion defaultExpanded>
			<AccordionSummary expandIcon={<IconSeeker icon="chevron-down" sx={{ fontSize: 14, color: dsColorNew.textMuted }}/>}
			                  sx={{ px: 2.5 }}>
				<AppCardTitle icon="table" title={`Daily breakdown (${rows.length} days)`}/>
			</AccordionSummary>
			<AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
				<TableContainer sx={{ maxHeight: 400 }}>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell><strong>Date</strong></TableCell>
								<TableCell><strong>Kind</strong></TableCell>
								<TableCell align="right"><strong>{activity}</strong></TableCell>
								<TableCell align="right"><strong>Same day PY</strong></TableCell>
								<TableCell align="right"><strong>Δ vs PY</strong></TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((r, i) => (
								<TableRow key={i} hover>
									<TableCell sx={{ whiteSpace: 'nowrap' }}>{displayDay(r.date)}</TableCell>
									<TableCell>
										<AppValueChip tone={r.kind === 'historic' ? 'default' : 'accent'}>{r.kind}</AppValueChip>
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 600 }}>
										{fmtNum(r.value)}
									</TableCell>
									<TableCell align="right">
										{r.py != null ? fmtNum(r.py) : '—'}
									</TableCell>
									<TableCell align="right">
										{r.delta != null ? (
											<Chip size="small" variant="filled"
											      color={r.delta > 0 ? 'success' : r.delta < 0 ? 'error' : 'default'}
											      label={`${r.delta > 0 ? '+' : ''}${r.delta}%`}
											      sx={{ fontWeight: 600, fontSize: 12 }}/>
										) : '—'}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</AccordionDetails>
		</Accordion>
	)
}
